import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardNavBar from "../DashboardNavBar";
import Footer from "../../../app/landing/Footer";
import MessageList from "./MessageList";
import MessageDetail from "./MessageDetail";
import MessageFilters from "./MessageFilters";
import { apiFetch } from "../../../core/utils/api";
import { PrimaryButton, SecondaryButton, Container } from "../../../design-system";
import { typography, surfaces, borders, textColors, effects } from "../../../design-system/tokens";

const Messages = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    website: "",
    unreadOnly: false,
  });
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [navbarRefreshTrigger, setNavbarRefreshTrigger] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileView, setMobileView] = useState("list"); // "list" | "detail"
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Fetch all website containers for the user
  const [userContainers, setUserContainers] = useState([]);
  const [hasLoadedContainers, setHasLoadedContainers] = useState(false);
  const [hasAnyMessages, setHasAnyMessages] = useState(false);

  useEffect(() => {
    fetchUserContainers();
  }, []);

  // Handle mobile detection (avoid per-pixel resize re-renders; update only when crossing breakpoint)
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;

    const mql = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile((prev) => (prev === mql.matches ? prev : mql.matches));

    update();

    // Modern browsers
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", update);
      return () => mql.removeEventListener("change", update);
    }

    // Safari < 14
    mql.addListener(update);
    return () => mql.removeListener(update);
  }, []);

  useEffect(() => {
    if (hasLoadedContainers) {
      fetchMessages();
    }
  }, [hasLoadedContainers, userContainers, filters]);

  const fetchUserContainers = async () => {
    try {
      const response = await apiFetch("/api/websites/", {
        credentials: "include",
      });
      if (response.ok) {
        const websites = await response.json();
        // Extract unique containers
        const containers = websites.reduce((acc, website) => {
          if (website.container_id && !acc.find(c => c.id === website.container_id)) {
            acc.push({
              id: website.container_id,
              name: website.container_name,
              domain: website.domain,
            });
          }
          return acc;
        }, []);
        setUserContainers(containers);
      } else {
        setUserContainers([]);
      }
    } catch (err) {
      setUserContainers([]);
      console.error("Failed to fetch user containers:", err);
    } finally {
      setHasLoadedContainers(true);
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    setError("");
    
    try {
      const stripBoldMarkers = (s) => String(s || "").replace(/\*\*/g, "").trim();
      const normalizeContact = (msg) => ({
        ...msg,
        kind: "contact",
        raw_id: msg.id,
        id: `contact:${msg.id}`,
        title: null,
        body_markdown: null,
      });
      const normalizeSystem = (msg) => ({
        kind: "system",
        raw_id: msg.id,
        id: `system:${msg.id}`,
        sender_display_name: msg.sender_display_name || "MyVitae",
        sender_email: null,
        sender_phone: null,
        website_container: null,
        website_container_name: "MyVitae",
        website_container_domain: null,
        owner_username: username,
        tags: "",
        created_at: msg.created_at,
        updated_at: msg.updated_at,
        is_read: !!msg.is_read,
        title: msg.title || "",
        body_markdown: msg.body_markdown || "",
        // Reuse existing UI fields
        message: msg.title
          ? `${msg.title} — ${stripBoldMarkers(msg.body_markdown).slice(0, 180)}`
          : stripBoldMarkers(msg.body_markdown).slice(0, 180),
      });

      // Fetch messages from all user's containers (contact)
      const allMessages = [];
      
      for (const container of userContainers) {
        const queryParams = new URLSearchParams();
        if (filters.unreadOnly) queryParams.append('is_read', 'false');
        
        const response = await apiFetch(
          `/api/containers/${container.id}/contact/?${queryParams}`,
          { credentials: "include" }
        );
        
        if (response.ok) {
          const containerMessages = await response.json();
          // Debug: console.log(`Fetched ${containerMessages.length} messages from container ${container.id}`);
          allMessages.push(...containerMessages.map(normalizeContact));
        }
      }

      // Fetch system inbox messages (MyVitae -> user)
      try {
        const queryParams = new URLSearchParams();
        if (filters.unreadOnly) queryParams.append("is_read", "false");
        const systemResp = await apiFetch(`/api/inbox/messages/?${queryParams}`, { credentials: "include" });
        if (systemResp.ok) {
          const data = await systemResp.json();
          const systemMessages = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
          allMessages.push(...systemMessages.map(normalizeSystem));
        }
      } catch (e) {
        // Non-fatal: still show contact messages
        console.error("Failed to fetch system inbox messages:", e);
      }

      setHasAnyMessages(allMessages.length > 0);
      
      // Apply client-side filters
      let filteredMessages = allMessages;
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredMessages = filteredMessages.filter(msg =>
          msg.sender_name?.toLowerCase().includes(searchLower) ||
          msg.sender_email?.toLowerCase().includes(searchLower) ||
          msg.message?.toLowerCase().includes(searchLower) ||
          msg.title?.toLowerCase().includes(searchLower) ||
          msg.body_markdown?.toLowerCase().includes(searchLower) ||
          msg.sender_display_name?.toLowerCase().includes(searchLower)
        );
      }
      
      if (filters.website) {
        // Website filter applies to contact messages only
        filteredMessages = filteredMessages.filter(msg =>
          msg.kind === "contact" && msg.website_container === parseInt(filters.website)
        );
      }
      
      // Sort by creation date (newest first)
      filteredMessages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setMessages(filteredMessages);
      setSelectedMessages((prev) => {
        const visibleIds = new Set(filteredMessages.map((msg) => msg.id));
        return new Set(Array.from(prev).filter((id) => visibleIds.has(id)));
      });
      
      const isMobileViewport =
        typeof window !== "undefined" && typeof window.matchMedia === "function"
          ? window.matchMedia("(max-width: 767px)").matches
          : isMobile;

      if (filteredMessages.length === 0) {
        setSelectedMessage(null);
        if (isMobileViewport) setMobileView("list");
      } else {
        const selectedStillVisible = filteredMessages.some((msg) => msg.id === selectedMessage?.id);
        if (!selectedStillVisible) {
          if (!isMobileViewport) {
            handleMessageSelect(filteredMessages[0], true);
          } else {
            setSelectedMessage(null);
            setMobileView("list");
          }
        }
      }
      
    } catch (err) {
      setError("Failed to load messages. Please try again.");
      console.error("Error fetching messages:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleMessageSelect = (message, autoSelected = false) => {
    setSelectedMessage(message);
    if (isMobile) setMobileView("detail");
    
    // Only mark as read when explicitly selected by user (not auto-selected)
    if (!autoSelected && !message.is_read) {
      markMessageAsRead(message);
    }
  };

  const parseUid = (uid) => {
    const s = String(uid || "");
    const [kind, raw] = s.split(":");
    const rawId = raw != null ? parseInt(raw, 10) : null;
    return { kind, rawId };
  };

  const markMessageAsRead = async (message) => {
    try {
      const { kind, rawId } = parseUid(message?.id);
      if (!rawId) return;

      const endpoint =
        kind === "system" ? `/api/inbox/messages/${rawId}/` : `/api/contact-messages/${rawId}/`;

      const response = await apiFetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_read: true }),
        credentials: "include",
      });
      
      if (response.ok) {
        const updatedMessage = await response.json();
        const updated =
          kind === "system"
            ? { ...message, is_read: true }
            : { ...message, ...updatedMessage, is_read: true };
        
        // Update the message in the list
        setMessages(msgs => msgs.map(msg => 
          msg.id === message.id ? updated : msg
        ));
        
        // Update selected message if it's the same one
        if (selectedMessage?.id === message.id) {
          setSelectedMessage(prev => ({ ...prev, is_read: true }));
        }
        
        // Trigger navbar refresh
        setNavbarRefreshTrigger(prev => prev + 1);
      } else {
        console.error(`Failed to mark message as read:`, response.status, response.statusText);
      }
    } catch (err) {
      console.error("Failed to mark message as read:", err);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedMessages.size === 0) return;
    
    setBulkActionLoading(true);
    
    try {
      const promises = Array.from(selectedMessages).map((uid) => {
        const { kind, rawId } = parseUid(uid);
        if (!rawId) return Promise.resolve(null);
        const endpoint = kind === "system" ? `/api/inbox/messages/${rawId}/` : `/api/contact-messages/${rawId}/`;
        return apiFetch(endpoint, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_read: action === 'read' }),
          credentials: "include",
        });
      });
      
      await Promise.all(promises);
      
      // Update messages state
      setMessages(msgs => msgs.map(msg => 
        selectedMessages.has(msg.id)
          ? { ...msg, is_read: action === 'read' }
          : msg
      ));
      
      // Clear selection
      setSelectedMessages(new Set());
      
      // Trigger navbar refresh
      setNavbarRefreshTrigger(prev => prev + 1);
      
    } catch (err) {
      console.error(`Failed to ${action} messages:`, err);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleUpdateMessage = (updatedMessage) => {
    setMessages(msgs => msgs.map(msg => 
      msg.id === updatedMessage.id ? updatedMessage : msg
    ));
    
    if (selectedMessage?.id === updatedMessage.id) {
      setSelectedMessage(updatedMessage);
    }
    
    // Trigger navbar refresh if read status changed
    setNavbarRefreshTrigger(prev => prev + 1);
  };

  const unreadCount = messages.filter(msg => !msg.is_read).length;
  const showInboxEmptyState = !loading && !error && !hasAnyMessages;

  return (
    <div className={`min-h-screen ${surfaces.modernApp} flex flex-col transition-colors duration-300 relative`}>
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className={effects.backgroundGlowBlue}></div>
        <div className={effects.backgroundGlowSky}></div>
      </div>
      <DashboardNavBar username={username} refreshUnreadCount={navbarRefreshTrigger} />
      
      <div className="flex-1 flex py-6 pt-20 relative z-10">
        {/* Main Content */}
        <Container size="7xl" className="flex-1 flex flex-col">
          {/* Header */}
          <div className={`${surfaces.primary} shadow-sm border-b ${borders.default} px-6 py-4`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h1 className={`${typography.h2} ${textColors.primary}`}>Messages</h1>
                <p className={`${typography.body} ${textColors.secondary}`}>
                  {messages.length} total messages
                  {unreadCount > 0 && (
                    <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full ${typography.caption} bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300`}>
                      {unreadCount} unread
                    </span>
                  )}
                </p>

                {/* Mobile: detail view actions (below title) */}
                {isMobile && mobileView === "detail" && (
                  <div className="mt-3 grid grid-cols-1 min-[360px]:grid-cols-2 gap-2">
                    <SecondaryButton
                      onClick={() => {
                        setFiltersOpen(false);
                        setMobileView("list");
                      }}
                      size="sm"
                      fullWidth
                      className="whitespace-nowrap"
                    >
                      View all messages
                    </SecondaryButton>
                    <SecondaryButton
                      onClick={() => navigate(`/edit/${username}`)}
                      size="sm"
                      fullWidth
                      className="whitespace-nowrap"
                    >
                      Dashboard
                    </SecondaryButton>
                  </div>
                )}
              </div>
              {/* Mobile controls */}
              {isMobile ? (
                <div className="flex items-center space-x-2">
                  {!showInboxEmptyState && mobileView === "list" && (
                    <SecondaryButton
                      onClick={() => setFiltersOpen((v) => !v)}
                      size="sm"
                      aria-expanded={filtersOpen}
                      aria-controls="mobile-filters"
                      className="whitespace-nowrap"
                    >
                      {filtersOpen ? "Hide Filters" : "Filters"}
                    </SecondaryButton>
                  )}
                </div>
              ) : (
                selectedMessages.size > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className={typography.bodySmall}>
                      {selectedMessages.size} selected
                    </span>
                    <PrimaryButton
                      onClick={() => handleBulkAction('read')}
                      disabled={bulkActionLoading}
                      size="sm"
                      variant="success"
                    >
                      Mark Read
                    </PrimaryButton>
                    <SecondaryButton
                      onClick={() => handleBulkAction('unread')}
                      disabled={bulkActionLoading}
                      size="sm"
                    >
                      Mark Unread
                    </SecondaryButton>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Filters */}
          {!showInboxEmptyState && !isMobile && (
            <MessageFilters
              filters={filters}
              setFilters={setFilters}
              userContainers={userContainers}
              messages={messages}
            />
          )}
          {!showInboxEmptyState && isMobile && filtersOpen && (
            <div id="mobile-filters" className={`px-4 py-3 ${surfaces.primary} border-b ${borders.default}`}>
              <MessageFilters
                filters={filters}
                setFilters={setFilters}
                userContainers={userContainers}
                messages={messages}
              />
            </div>
          )}

          {/* Content Area */}
          {showInboxEmptyState ? (
            <div className={`flex-1 ${surfaces.primary} flex items-center justify-center px-6`}>
              <div className="mx-auto max-w-2xl py-16 text-center">
                <div className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full ${surfaces.tertiary} ${textColors.tertiary}`}>
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661Z"
                    />
                  </svg>
                </div>
                <h2 className={`${typography.h4} ${textColors.primary}`}>No messages yet</h2>
                <p className={`mt-3 ${typography.body} ${textColors.secondary}`}>
                  When people send you messages through your website, they&apos;ll appear here.
                </p>
                <p className={`mt-2 ${typography.bodySmall} ${textColors.tertiary}`}>
                  This inbox is where you&apos;ll read new messages, check who sent them, and keep track of conversations coming from your site.
                </p>
                <div className="mt-6 flex justify-center">
                  <SecondaryButton
                    onClick={() => navigate(`/edit/${username}`)}
                    size="sm"
                    className="whitespace-nowrap"
                  >
                    Back to dashboard
                  </SecondaryButton>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex">
              {/* Desktop/Table layout */}
              {!isMobile && (
                <>
                  <div className={`w-1/3 border-r ${borders.default} ${surfaces.primary}`}>
                    <MessageList
                      messages={messages}
                      selectedMessage={selectedMessage}
                      onMessageSelect={handleMessageSelect}
                      loading={loading}
                      error={error}
                      selectedMessages={selectedMessages}
                      setSelectedMessages={setSelectedMessages}
                    />
                  </div>
                  <div className={`flex-1 ${surfaces.primary}`}>
                    <MessageDetail
                      message={selectedMessage}
                      onUpdateMessage={handleUpdateMessage}
                      userContainers={userContainers}
                    />
                  </div>
                </>
              )}

              {/* Mobile layout: swap views */}
              {isMobile && (
                <div className={`w-full ${surfaces.primary}`}>
                  {mobileView === "list" ? (
                    <MessageList
                      messages={messages}
                      selectedMessage={selectedMessage}
                      onMessageSelect={handleMessageSelect}
                      loading={loading}
                      error={error}
                      selectedMessages={selectedMessages}
                      setSelectedMessages={setSelectedMessages}
                    />
                  ) : (
                    <MessageDetail
                      message={selectedMessage}
                      onUpdateMessage={handleUpdateMessage}
                      userContainers={userContainers}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </Container>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  );
};

export default Messages; 